'use client';

import { useState, useEffect, useMemo } from 'react';
import DataTable from '../../../../components/DataTable';
import ActionMenu from '../../../../components/ActionMenu';
import { useNotifier } from '../../../../components/Notifier';
import DeleteConfirmDialog from '../../../../components/DeleteConfirmDialog';

import ConstructionTypeAddEdit from './ConstructionTypeAddEdit';
import { useLanguage } from '../../../../utils/i18n';
import { useConstructionTypeDataList, deleteConstructionType } from '../../../../stores/constructionTypeStore';
import { fleetApi } from '../../../../api/axiosClient';
import { useCompanyList } from '../../../../stores/companyStore';

function ConstructionTypePageContent() {
  const currentUserRole = typeof window !== 'undefined' ? localStorage.getItem('rolename') || localStorage.getItem('role_name') || '' : '';
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const notify = useNotifier();
  const { t } = useLanguage();

  const [searchField, setSearchField] = useState('name');
  const [searchRaw, setSearchRaw] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null;
      return stored ? [{ value: String(stored), label: String(stored) }] : [];
    } catch {
      return [];
    }
  });

  const { companyOptions } = useCompanyList();

  const companyOptionsNormalized = useMemo(
    () =>
      (companyOptions || []).map((c) => ({
        value: String(c.value ?? c.id ?? c),
        label: c.label ?? c.name ?? String(c.value ?? c.id ?? c)
      })),
    [companyOptions]
  );

  useEffect(() => {
    try {
      if (!companyOptionsNormalized || companyOptionsNormalized.length === 0) return;
      const raw = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
      if (!raw) return;
      let u = null;
      try {
        u = JSON.parse(raw);
      } catch {
        return;
      }
      const companyId = u?.company ?? u?.company_id ?? null;
      if (companyId == null) return;
      const companyIdStr = String(companyId);
      const match = companyOptionsNormalized.find((o) => String(o.value) === companyIdStr);
      const selected = match
        ? [{ value: String(match.value), label: match.label ?? String(match.value) }]
        : [{ value: companyIdStr, label: companyIdStr }];
      setSelectedCompanies((prev) => {
        const prevVal = prev && prev[0] && String(prev[0].value);
        if (prevVal === selected[0].value) return prev;
        return selected;
      });
    } catch (e) {
      console.error('auto select company from user failed', e);
    }
  }, [companyOptionsNormalized]);

  const searchFieldOptions = useMemo(
    () => [
      { value: 'all', label: t('general.searchAll', 'All') },
      { value: 'company', label: t('general.company', 'Company') },
      { value: 'name', label: t('general.name', 'Name') },
      { value: 'description', label: t('general.description', 'Description') }
    ],
    [t]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchValue(String(searchRaw ?? '').trim());
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 700);
    return () => clearTimeout(timeoutId);
  }, [searchRaw, searchField]);

  const {
    data: rows,
    totalRecord: totalRecords,
    isLoading,
    refetch
  } = useConstructionTypeDataList(paginationModel.page, paginationModel.pageSize, selectedCompanies, searchField, searchValue);

  const handleFetchAllForExport = async () => {
    try {
      const length = totalRecords > 0 ? totalRecords : 9999;
      const columns = [];
      const fetchFilters = [];

      if (searchRaw && searchRaw.trim() !== '') {
        if (searchField === 'all') {
          fetchFilters.push(
            { name: 'name', value: searchRaw },
            { name: 'description', value: searchRaw },
            { name: 'name', table_name: 'm_company', value: searchRaw }
          );
        } else {
          if (searchField === 'company') {
            fetchFilters.push({ name: 'name', table_name: 'm_company', logic_operator: 'like', operator: 'AND', value: searchRaw });
          } else {
            fetchFilters.push({ name: searchField, logic_operator: 'like', operator: 'AND', value: searchRaw });
          }
        }
      }

      const payload = {
        paging: { start: 0, length },
        orders: { columns: ['name'], ascending: true },
        joins: [{ name: 'company', column_results: ['name'] }],
        columns: columns
      };

      if (fetchFilters.length > 0) {
        if (searchField === 'all') {
          payload.filters = fetchFilters;
        } else {
          payload.columns = [...payload.columns, ...fetchFilters];
        }
      }

      const companies = (selectedCompanies || []).map((c) => c.value).join(',');
      if (companies) {
        payload.columns.push({ name: 'company_id', logic_operator: 'in', operator: 'AND', value: companies });
      }

      const resp = await fleetApi.post('/construction-types/search', payload);
      const data = resp?.data ?? null;
      return Array.isArray(data) ? data : (data?.data ?? []);
    } catch (err) {
      console.error('construction type export all error', err);
      return [];
    }
  };

  const handlePageChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const handleCompanyChange = (companies) => {
    setSelectedCompanies(companies);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleAddClick = () => {
    setEditingId(null);
    setOpenDialog(true);
  };

  const handleEditClick = (id) => {
    setEditingId(id);
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargetId, setConfirmTargetId] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleDelete = (id, itemName) => {
    setConfirmTargetId(id);
    setConfirmName(itemName || '');
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmTargetId(null);
    setConfirmName('');
  };

  const handleConfirmDelete = async () => {
    if (!confirmTargetId) return;
    setConfirmLoading(true);
    try {
      await deleteConstructionType(confirmTargetId);
      notify({ message: t('general.deleteSuccess', 'Data successfully deleted'), severity: 'success' });
      refetch();
    } catch (err) {
      console.error(err);
      notify({ message: t('general.deleteFailed', 'Failed to delete data'), severity: 'error' });
    } finally {
      setConfirmLoading(false);
      setConfirmOpen(false);
      setConfirmTargetId(null);
      setConfirmName('');
    }
  };

  const columns = [
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      valueGetter: (value, row) => row?.join?.company_name || row?.company_name || '-'
    },
    { field: 'name', headerName: t('general.name', 'Name'), flex: 1 },
    { field: 'description', headerName: t('general.description', 'Description'), flex: 1.5 },
    ...(currentUserRole === 'Administrator'
      ? [
          { field: 'created_by', headerName: t('general.createdBy', 'Created By'), flex: 0.5, hide: true },
          { field: 'created_date', headerName: t('general.createdDate', 'Created Date'), flex: 0.5, hide: true },
          { field: 'modified_by', headerName: t('general.modifiedBy', 'Modified By'), flex: 0.5, hide: true },
          { field: 'modified_date', headerName: t('general.modifiedDate', 'Modified Date'), flex: 0.5, hide: true }
        ]
      : []),
    {
      field: 'actions',
      headerName: t('general.actions', 'Actions'),
      sortable: false,
      headerAlign: 'center',
      width: 120,
      renderCell: (params) => (
        <ActionMenu row={params.row} onEdit={handleEditClick} onDelete={(id) => handleDelete(id, params.row.name)} mode="inline" />
      )
    }
  ];

  return (
    <div>
      <DataTable
        title={t('constructionType.title', 'Construction Type')}
        onFetchAllForExport={handleFetchAllForExport}
        rows={rows || []}
        columns={columns}
        loading={isLoading}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationMode="server"
        rowCount={totalRecords}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePageChange}
        searchQuery={searchRaw}
        onSearch={({ field, value }) => {
          setSearchField(field);
          setSearchRaw(value ?? '');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        searchFieldOptions={searchFieldOptions}
        selectedSearchField={searchField}
        onSearchFieldChange={(f) => {
          setSearchField(f);
          setSearchRaw('');
          setSearchValue('');
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
        onAddClick={handleAddClick}
        searchLabel={t('general.search', 'Search...')}
        companyOptions={companyOptionsNormalized}
        selectedCompanies={selectedCompanies}
        onCompanyChange={handleCompanyChange}
      />

      {openDialog && <ConstructionTypeAddEdit open={openDialog} id={editingId} onClose={handleDialogClose} onSuccess={() => refetch()} />}

      <DeleteConfirmDialog
        open={confirmOpen}
        loading={confirmLoading}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmDelete}
        title={t('deleteConfirm.title', 'Confirm Delete')}
        message={t('deleteConfirm.message', 'Are you sure you want to delete this item?')}
        confirmLabel={t('deleteConfirm.label', 'Delete')}
        cancelLabel={t('deleteConfirm.cancel', 'Cancel')}
        itemName={confirmName}
      />
    </div>
  );
}

export default function ConstructionTypePage() {
  return <ConstructionTypePageContent />;
}
